import React, { useEffect, useReducer, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Box, Typography, Skeleton } from '@mui/material';
import { parseISO, format } from 'date-fns';
import { CustomReactSelect,
    CustomButton,
    CustomModal,
    CustomCalendar } from '../../../shared';
import { CalendarIcon, CrossCircleIcon } from '../../../../assets';
import { actions as triggerActions } from '../../../../redux/reducers/triggerReducer';

const FilterBar = () => {
    const dispatch = useDispatch();
    const {
        companyOptions, company,isOmaniChannel 
    } = useSelector((state) => state.company);
    const { triggerSummary } = useSelector((state) => state.trigger);
    const { startDateRange, endDateRange } = useSelector((state) => state.filter);
    const dateRange = `${format(
        parseISO(startDateRange),
        'MMM dd, yyyy'
    )} -  ${format(parseISO(endDateRange), 'MMM dd, yyyy')}`;
    const {
        triggerNames,totalTriggers, isTriggerSummaryGraphsLoading,isDownloading, isTriggerSummaryPaginated
    } = triggerSummary;
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [triggerOptions, setTriggerOptions] = useState([
        {
            label: 'All',
            value: 'All'
        }
    ]);
    const [selectedTrigger, setSelectedTrigger] = useState({
        label: 'All',
        value: 'All'
    });
    const [selectedTriggersToShow, setSelectedTriggersToShow] = useState(['All']);
    const showAudience = [66,161].includes(company.id) || isOmaniChannel;
    const createTriggerOptions = () => {
        const options = triggerNames.map((item) => ({
            label: item,
            value: item
        }));
        setTriggerOptions((prevOptions) => {
            const newOptions = options.filter(
                (newOption) =>
                    !prevOptions.some(
                        (existingOption) =>
                            existingOption.value === newOption.value
                    )
            );
            return [...prevOptions, ...newOptions];
        });
    };
    

    const handleShowCalendar = () => {
        setCalendarOpen(!calendarOpen);
    };

    const handleTriggerSelection = (option) => {
        setSelectedTrigger(option);
        dispatch(triggerActions.setSelectedTriggers({ option: option }));
        setSelectedTriggersToShow(prev => 
            option.value === 'All' 
                ? ['All'] 
                : prev.includes('All') 
                    ? [option.value] 
                    : [...prev, option.value]
        );
    };

    const handleRemoveTrigger = (trigger) => {
        dispatch(triggerActions.handleRemoveSelectedTrigger({ trigger: trigger }));
        setSelectedTriggersToShow((prev) => {
            const updatedTriggers = prev.filter((item) => item !== trigger);
            if (updatedTriggers.length === 0) {
                setSelectedTrigger({ label: 'All', value: 'All' });
                return ['All'];
            }
            
            return updatedTriggers;
        });
    };

    useEffect(() => {
        if (triggerNames.length) {
            createTriggerOptions();
        }
    }, [triggerNames]);
    useEffect(() => {
        setSelectedTriggersToShow(['All']);
        setSelectedTrigger({
            label: 'All',
            value: 'All'
        });
    }, [startDateRange,endDateRange]);

    const renderSelectedTriggers = () => [...new Set(selectedTriggersToShow)]?.map((trigger) => (
        <Box className="br-16px bg-primary-50 padding-10px" key={trigger}>
            <Box className="flex-row-center gap-8">
                <Typography className="text-sm-medium gray-900">
                    {trigger}
                </Typography>
                {
                    (isDownloading || (isTriggerSummaryGraphsLoading || isTriggerSummaryPaginated)) ? null :
                        <Box
                            className="icon"
                            onClick={() => handleRemoveTrigger(trigger)}
                        >
                            {
                                selectedTriggersToShow.includes('All') ? null : <img alt={trigger} src={CrossCircleIcon} />
                            }
                        </Box>
                }
                
            </Box>
        </Box>
    ));

    return (
        <Grid container className="flex-row-space-between mb-24px mt-10px">
            <Grid item xs={8}>
                <Box className="flex-row gap-12">
                    <Box className="w-50">
                        <CustomReactSelect
                            customStyle={{ borderRadius: '50px' }}
                            disable = {isDownloading || (isTriggerSummaryGraphsLoading || isTriggerSummaryPaginated)}
                            handleFilter={handleTriggerSelection}
                            isOptionsSearchable={true}
                            options={triggerOptions}
                            placeholder="Select Triggers"
                            selectedOption={selectedTrigger}
                        />
                    </Box>
                    {/* Select Inputs */}
                    <Box className="w-50">
                        <CustomButton
                            buttonColor="#FFFFFF"
                            buttonDisabled={isDownloading || (isTriggerSummaryGraphsLoading || isTriggerSummaryPaginated)}
                            data_test_id="dashboard_date_button"
                            handleClick={handleShowCalendar}
                            icon={<CalendarIcon />}
                            title={dateRange}
                            titleColor="#344054"
                        />
                        <CustomModal
                            component={
                                <Box className="custom-modal-calendar-container">
                                    <CustomCalendar
                                        closeModal={handleShowCalendar}
                                    />
                                </Box>
                            }
                            data_test_id="dashboard_calendar_modal"
                            handleClose={handleShowCalendar}
                            modalOpen={calendarOpen}
                            onClose={handleShowCalendar}
                        />
                    </Box>
                </Box>
            </Grid>
            <Grid item className="flex-row-justify-end gap-12" xs={4}>
                <Box className="flex-row-space-between bg-primary-50 br-16px gap-12 padding-10px height-40px">
                    <Box className="gap-8">
                        <Typography className="text-sm-medium gray-500">
                            {showAudience ?'Audience Count :' : 'Trigger Count :'}
                        </Typography>
                    </Box>
                    <Box>
                        {
                            isTriggerSummaryGraphsLoading ? <Skeleton height={30} variant='text' width={40} /> :
                                <Typography className="text-sm-semi-bold gray-900">
                                    {totalTriggers}
                                </Typography>
                        }
                        
                    </Box>
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Box className="flex-row flex-wrap gap-16 mt-2">{renderSelectedTriggers()}</Box>
            </Grid>
        </Grid>
    );
};

export default FilterBar;
